<template>
    <PageSection fixed>
        <PageHeader id="page-header" title="Supplies">
        </PageHeader>
        <SupplyCancellationDialog :open="isCancelling" :submitting="isCancelSubmitting" @close="closeCancelDialog"
            @confirm="confirmCancel" />
        <SupplyEditDialog :data="selectedRows ?? []" :open="isEditing" :submitting="isSubmitting"
            @close="closeEditDialog" @confirm="(data) => confirmEdit(data)" />
      	<Card>
            <TableLayout>
                <template #toolbar>
                    <div class="table-toolbar b-0">
                        <div class="flex-grow">
                            <div class="flex-row justify-between">
                                <SearchBar class="h-8 m-0 min-w-xs" placeholder="Search Product name or Style number"
                                    @search="fetch({ searchText: $event })" :initial-search="searchText" />
                                <Button
                                    class="focus:outline-none outline-none w-[fit-content] hover:bg-purple-50 items-center rounded-2 inline-flex p-2 font-secondary"
                                    @click="() => router.push(getRequestPageRoute(institutionConfig.approval_config, isEAorIA))"
                                    variant="header">
                                    <component :is="PlusIcon" />
                                    <span class="pl-[0.25rem] font-semibold text-sm">
                                        New
                                    </span>
                                </Button>
                                <Button
                                    class="focus:outline-none outline-none w-[fit-content] hover:bg-purple-50 items-center rounded-2 inline-flex p-2 font-secondary"
                                    @click="download()" :disabled="downloading" :loading="downloading" variant="header">
                                    <component :is="DownloadIcon" />
                                    <span class="pl-[0.25rem] font-semibold text-sm">
                                        Download
                                    </span>
                                </Button>
                            </div>
                        </div>
                    </div>
                </template>
                <Table :table="table" :show-loading-spinner="loading" aria-describedby="page-header" class="b-l-style-none b-r-style-none b-b-style-solid">
                    <thead class=" table-thead">
                        <tr class="flex" v-for="headerGroup in table.getHeaderGroups()" :key="headerGroup.id">
                            <TableHeaderCell v-for="header in headerGroup.headers" :header="header">
                                <template #selector>
                                    <RowSelector :row="table"></RowSelector>
                                </template>
                            </TableHeaderCell>
                        </tr>
                    </thead>
                    <tbody>
                        <tr class="table-row" v-for="row in rows" :key="row.id">
                            <TableDataCell v-for="cell in row.getVisibleCells()" :cell="cell">
                                <template #selector>
                                    <RowSelector :row="row"></RowSelector>
                                </template>
                
                                <template #notes>
                                    <div>
                                        <Tooltip position="bottom" extraToolTipClass="max-w-xl">
                                            <span>
                                                {{ cell.row.original.notes }}
                                            </span>
                                            <template #tooltip-content>
                                                {{ cell.row.original.notes }}
                                            </template>
                                        </Tooltip>
                                    </div>
                                </template>
                                <template #supply_status>
                                    <Tag :variant="getCustomerStatusChipType(cell.row.original.supply_status)"
                                        :clearable="false">
                                        {{ cell.row.original.supply_status
                                        }}
                                    </Tag>
                                </template>
                
                                <template #actions>
                                    <Menu placement="auto" :items="getRowActions(cell)" class="cell-menu" is-icon />
                                </template>
                
                            </TableDataCell>
                        </tr>
                    </tbody>
                    <template #empty>
                        <EmptyState :icon="message.icon" :title="message.title" :text="message.description">
                        </EmptyState>
                    </template>
                </Table>
                <template #paginator>
                    <Paginator :table="table" :page-sizes="[5, 10, 25, 50, 100, 200]" class="b-0" />
                </template>
            </TableLayout>
				</Card>
    </PageSection>
</template>

<script lang="ts" setup>
import {
	Cell,
	functionalUpdate,
	getCoreRowModel,
	useVueTable,
} from "@tanstack/vue-table";
import { useRouter } from "vue-router";
import { definePage } from "vue-router/auto";
import DownloadIcon from "./../../../../../shared/assets/icons/download.svg?component";
import PlusIcon from "./../../../../../shared/assets/icons/plus.svg?component";
import Button from "../../../../../shared/components/Button.vue";
import { useGuardedTableColumns } from "../../../../../shared/components/ComposableTable/useGuardedTableColumns";
import { MenuItemData } from "../../../../../shared/components/Menu.vue";
import SupplyCancellationDialog from "../../../components/Supply/SupplyCancellationDialog.vue";
import SupplyEditDialog from "../../../components/Supply/SupplyEditDialog.vue";
import { useInstitution } from "../../../composables/useInstitution";
import { useSupplyTable } from "../../../composables/useSupplyTable";
import { getStatusChipType as getCustomerStatusChipType } from "../../../config";
import { useAcademicDates } from "../../../stores/academic-dates";
import { SupplyRow } from "../../../trpc/routers/supplies";
import { generateTableColumns } from "../../../utils/booklist-table-columns";
import { getRequestPageRoute } from "../../../utils/request-page-route";

const { institutionConfig, isEAorIA } = useInstitution();

const router = useRouter();

const {
	data,
	columns: supplyTableColumns,
	pagination,
	totalRowCount,
	searchText,
	filters,
	sorting,
	loading,
	message,
	selectedRows,
	isEditing,
	isCancelling,
	isCancelSubmitting,
	isSubmitting,
	columnPinning,
	downloading,
	fetch,
	cancel,
	confirmCancel,
	closeCancelDialog,
	closeEditDialog,
	confirmEdit,
	edit,
	download,
} = useSupplyTable();

const columns = useGuardedTableColumns<SupplyRow>((helper) => [
	...generateTableColumns(supplyTableColumns.value, helper),
	{
		columnDef: helper.display({
			id: "actions",
			header: "Actions",
			size: 72,
			enableSorting: false,
			enableResizing: false,
			enableHiding: false,
		}),
	},
]);

const table = useVueTable<SupplyRow>({
	getCoreRowModel: getCoreRowModel<SupplyRow>(),
	manualPagination: true,
	columnResizeMode: "onChange",
	getRowId: (row) => row.id,
	get data() {
		return data.value;
	},
	get columns() {
		return columns.value;
	},
	get rowCount() {
		return totalRowCount.value;
	},
	state: {
		get columnPinning() {
			return columnPinning.value;
		},
		get sorting() {
			return sorting.value;
		},
		get pagination() {
			return pagination.value;
		},
	},
	onPaginationChange(updater) {
		const newPagination = functionalUpdate(updater, pagination.value);

		fetch({
			page: newPagination.pageIndex + 1,
			rowsPerPage: newPagination.pageSize,
		});

		window.localStorage.setItem(
			"preferredSupplyTablePageSize",
			newPagination.pageSize.toString(),
		);
	},
	onSortingChange(updater) {
		const newSorting = functionalUpdate(updater, sorting.value);

		fetch({
			sorting: newSorting,
		});
	},
});

const rows = computed(() => table.getCoreRowModel().rows);
const academicDatesStore = useAcademicDates();

onMounted(() => {
	fetch({
		page: 1,
		filters: {
			startDate: academicDatesStore.selectedDates?.startDate || undefined,
			endDate: academicDatesStore.selectedDates?.endDate || undefined,
		},
	});
});

watch(
	() => academicDatesStore.selectedDates,
	(newValue) => {
		if (
			newValue?.startDate === filters.value.startDate &&
			newValue?.endDate === filters.value.endDate
		) {
			return;
		}

		// do not send empty string
		fetch({
			page: 1,
			filters: {
				startDate: academicDatesStore.selectedDates?.startDate || undefined,
				endDate: academicDatesStore.selectedDates?.endDate || undefined,
			},
		});
	},
);

function getRowActions(cell: Cell<SupplyRow, unknown>): MenuItemData[] {
	return [
		{
			label: "Edit",
			action: () => {
				edit([cell.row.original]);
			},
		},
		{
			label: "Cancel",
			action: () => {
				cancel([cell.row.original]);
			},
		},
	];
}

definePage({
	meta: {
		requiresAuth: true,
		permission: "booklist:view",
		customPermission: "hasSupplyPageAccess",
	},
	alias: ["/requests/supply", "/booklist/supply"],
});
</script>
